/**
 * Stable, user-facing error codes.
 *
 * These are the identifiers shown in the UI next to a failure message and
 * documented in `docs/error-codes.md`. They are deliberately the *same*
 * vocabulary already used as the `errorClass` attribute on telemetry spans
 * (`errorClassOf` in ai-client.ts, ADR-006), so a code a user reports from a
 * screenshot, a row in the docs, and a dashboard grouping all refer to one
 * thing.
 *
 * Because they get screenshotted into support threads and grouped on in
 * telemetry backends, treat a code as a compatibility surface: renaming one
 * breaks the docs and any existing dashboard. Add new codes freely; rename
 * existing ones only deliberately.
 *
 * These are codes, not messages. The human-readable text a user sees comes
 * from the `AiError` message; per CLAUDE.md A3/B3 that message is always an
 * authored string, never a raw internal error.
 */

/** Every code the app can surface. Adding a member here without a matching
 * row in `docs/error-codes.md` fails `tests/error-codes.test.ts`. */
export const ERROR_CODES = {
  NO_API_KEY: "No API key is configured and the endpoint is not a loopback address.",
  NETWORK_UNREACHABLE:
    "The provider could not be reached at all — CORS, a blocked private-network request, DNS, or a refused connection.",
  PROVIDER_HTTP_ERROR: "The provider answered with an HTTP error status.",
  PROVIDER_EMPTY_RESPONSE: "The provider returned 200 but no message in the response.",
  PROVIDER_NOT_STREAMABLE: "The provider's response had no readable body to stream.",
  INSIGHT_INVALID_JSON:
    "The model's reply to an email-analysis request was not the JSON object that request asks for.",
  NEEDS_CONSENT: "A Microsoft Graph scope needs consent before the operation can run.",
} as const;

/** Union of every valid error code. */
export type ErrorCode = keyof typeof ERROR_CODES;

/** All codes, for iteration in tests and docs tooling. */
export function allErrorCodes(): ErrorCode[] {
  return Object.keys(ERROR_CODES) as ErrorCode[];
}

/** Narrows an arbitrary string to a known code. Used at the UI boundary so an
 * unrecognized value is dropped rather than rendered. */
export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(ERROR_CODES, value);
}
